import stringify from "json-stringify-pretty-compact";
import JSON5 from "json5";
import React from "react";
import { cn } from "src/common/utils";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "src/components/ui/hover-card";
import { Markdown } from "src/components/ui/Markdown";

type Props = {
  text: string;
  hoverText?: string;
  maxLength?: number;
  renderer: (isTruncated: boolean, text: string) => React.ReactNode;
};

type MarkdownProps = {
  text: string;
  className?: string;
};

/**
 * TruncatedText - For plain text truncation with hover to see full text
 */
export function TruncatedText({ text, hoverText, maxLength = 400, renderer }: Props) {
  const isTruncated = text.length > maxLength || !!hoverText;
  const displayText = text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

  const hoverContent = hoverText ?? text;
  let isJson = true;
  let formattedContent = hoverContent;
  try {
    const parsed = JSON5.parse(hoverContent);
    formattedContent = stringify(parsed, { indent: 4 });
  } catch {
    isJson = false;
  }

  return (
    <HoverCard openDelay={50} closeDelay={50}>
      <HoverCardTrigger asChild>
        {renderer(isTruncated, displayText)}
      </HoverCardTrigger>
      {isTruncated && (
        <HoverCardContent className="w-[40rem] max-h-[32rem] overflow-y-auto">
          {isJson ? (
            <div className="text-sm font-mono whitespace-pre-wrap break-words">
              {formattedContent}
            </div>
          ) : (
            <div className="text-sm whitespace-pre-wrap break-words">
              <Markdown>{hoverContent}</Markdown>
            </div>
          )}
        </HoverCardContent>
      )}
    </HoverCard>
  );
}

/**
 * TruncatedMarkdown - For markdown content with single-line truncation and hover to see full rendered markdown
 */
export function TruncatedMarkdown({ text, className }: MarkdownProps) {
  // Consider truncated if text has newlines or is long
  const isTruncated = text.includes("\n") || text.length > 150;

  return (
    <HoverCard openDelay={50} closeDelay={50}>
      <HoverCardTrigger asChild>
        <div className={cn(
          "line-clamp-1 [&_p]:inline [&_p]:m-0 overflow-hidden text-ellipsis",
          isTruncated && "cursor-help hover:underline",
          className
        )}>
          <Markdown>{text}</Markdown>
        </div>
      </HoverCardTrigger>
      {isTruncated && (
        <HoverCardContent className="w-[40rem] max-h-[32rem] overflow-y-auto">
          <div className="text-sm">
            <Markdown className="!break-normal !hyphens-none ![overflow-wrap:normal] [&_a]:!break-normal [&_a]:![overflow-wrap:normal] [&_code]:!break-normal [&_*]:!break-normal">{text}</Markdown>
          </div>
        </HoverCardContent>
      )}
    </HoverCard>
  );
}